/**
 * Fixed Assets & Depreciation — Wave F.
 *
 * Tracks capitalized purchases (vehicles, equipment, large tools) and
 * generates the monthly depreciation journal entry that an accountant
 * would otherwise have to post by hand.
 *
 * Storage: in-memory list of FixedAsset records (createdInService on
 * `inServiceDate`, useful life in months, straight-line depreciation only —
 * the 95% case for a remodeler). Salvage value defaults to $0.
 *
 * Posting model (straight-line, monthly):
 *   monthlyDep = (cost - salvage) / lifeMonths
 *   DR 6920 Depreciation Expense / CR 1590 Accumulated Depreciation
 *
 * `postDepreciationFor(period)` is the sole writer. It is idempotent per
 * (assetId, period): re-running for the same yyyy-mm posts nothing extra.
 * Bulk runner `postDepreciationThroughCurrentMonth()` catches up any asset
 * whose `lastPostedPeriod` lags behind today.
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
} from "react";
import { useGeneralLedger } from "../GeneralLedgerContext";
import type { JournalEntry } from "../glTypes";

export type DepreciationMethod = "straight_line";

export interface FixedAsset {
  id: string;
  name: string;
  category: "vehicle" | "equipment" | "tools" | "leasehold_improvement" | "other";
  cost: number;
  salvageValue: number;
  lifeMonths: number;
  inServiceDate: string; // yyyy-mm-dd
  method: DepreciationMethod;
  /** GL account to debit each month — typically 6920 Depreciation Expense. */
  expenseAccountId: string;
  /** GL account to credit each month — typically 1590 Accumulated Depreciation. */
  accumulatedAccountId: string;
  /** yyyy-mm of the most recent successfully posted depreciation entry. */
  lastPostedPeriod?: string;
  /** Append-only history of JE ids posted for this asset. */
  postedEntries: Array<{ period: string; jeId: string; amount: number; postedAt: string }>;
  disposedAt?: string;          // ISO timestamp the disposal action was recorded
  disposalDate?: string;        // yyyy-mm-dd — accounting date of the disposal JE
  disposalReason?: string;
  disposalProceeds?: number;    // cash received (0 = scrapped)
  disposalJournalEntryId?: string;
  disposalGainLoss?: number;    // positive = gain, negative = loss
  createdAt: string;
  createdBy: string;
  notes?: string;
}

interface NewAssetInput {
  name: string;
  category: FixedAsset["category"];
  cost: number;
  salvageValue?: number;
  lifeMonths: number;
  inServiceDate: string;
  expenseAccountId?: string;
  accumulatedAccountId?: string;
  notes?: string;
  createdBy?: string;
}

interface DisposeInput {
  reason: string;
  disposalDate?: string;      // yyyy-mm-dd, defaults to today
  proceeds?: number;          // cash received, defaults to 0
  cashAccountId?: string;     // bank account credited with proceeds, defaults to 1100
}

interface FixedAssetsContextValue {
  assets: FixedAsset[];
  createAsset: (input: NewAssetInput) => FixedAsset;
  /** Records disposal AND posts the closing JE (asset cost out, accum dep in,
   *  cash in, gain/loss balancing). Idempotent per asset. */
  disposeAsset: (id: string, input: DisposeInput) => { asset: FixedAsset; journalEntry: JournalEntry | null };
  monthlyDepreciation: (asset: FixedAsset) => number;
  bookValue: (asset: FixedAsset, asOfIso?: string) => number;
  postDepreciationFor: (assetId: string, period: string) => JournalEntry | null;
  postDepreciationThroughCurrentMonth: () => { posted: JournalEntry[]; skipped: number };
}

const Ctx = createContext<FixedAssetsContextValue | null>(null);

const DEFAULT_EXPENSE_ACCT = "6920";
const DEFAULT_ACCUM_ACCT = "1590";
const today = () => new Date().toISOString().slice(0, 10);
const periodOf = (iso: string) => iso.slice(0, 7); // yyyy-mm
const nextPeriod = (p: string): string => {
  const [y, m] = p.split("-").map(Number);
  return new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 7); // m is 1-indexed → first of next month
};
const lastDayOfPeriod = (p: string): string => {
  const [y, m] = p.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
};

function monthsBetween(fromIso: string, toIso: string): number {
  const [fy, fm] = fromIso.slice(0, 7).split("-").map(Number);
  const [ty, tm] = toIso.slice(0, 7).split("-").map(Number);
  return (ty - fy) * 12 + (tm - fm);
}

export function FixedAssetsProvider({ children }: { children: React.ReactNode }) {
  const gl = useGeneralLedger();
  const [assets, setAssets] = useState<FixedAsset[]>([]);

  const monthlyDepreciation = useCallback((a: FixedAsset) => {
    if (a.lifeMonths <= 0) return 0;
    const depreciable = Math.max(0, a.cost - a.salvageValue);
    return Math.round((depreciable / a.lifeMonths) * 100) / 100;
  }, []);

  const bookValue = useCallback((a: FixedAsset, asOfIso = today()) => {
    if (a.disposedAt && a.disposedAt <= asOfIso) return 0;
    const monthsInService = Math.max(0, monthsBetween(a.inServiceDate, asOfIso));
    const fullyDepreciatedMonths = Math.min(a.lifeMonths, monthsInService);
    const accumulated = monthlyDepreciation(a) * fullyDepreciatedMonths;
    return Math.max(a.salvageValue, Math.round((a.cost - accumulated) * 100) / 100);
  }, [monthlyDepreciation]);

  const createAsset = useCallback((input: NewAssetInput): FixedAsset => {
    if (input.cost <= 0) throw new Error("Cost must be > 0.");
    if (input.lifeMonths <= 0) throw new Error("Useful life must be > 0 months.");
    const salvage = input.salvageValue ?? 0;
    if (salvage < 0 || salvage >= input.cost) throw new Error("Salvage must be 0 ≤ x < cost.");
    const asset: FixedAsset = {
      id: `fa_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: input.name.trim(),
      category: input.category,
      cost: input.cost,
      salvageValue: salvage,
      lifeMonths: input.lifeMonths,
      inServiceDate: input.inServiceDate,
      method: "straight_line",
      expenseAccountId: input.expenseAccountId ?? DEFAULT_EXPENSE_ACCT,
      accumulatedAccountId: input.accumulatedAccountId ?? DEFAULT_ACCUM_ACCT,
      postedEntries: [],
      createdAt: new Date().toISOString(),
      createdBy: input.createdBy ?? "Steve",
      notes: input.notes,
    };
    setAssets(prev => [asset, ...prev]);
    return asset;
  }, []);

  const disposeAsset = useCallback((id: string, input: DisposeInput) => {
    const asset = assets.find(a => a.id === id);
    if (!asset) throw new Error("Asset not found.");
    if (asset.disposedAt) throw new Error("Asset is already disposed.");

    const disposalDate = input.disposalDate ?? today();
    const proceeds = Math.max(0, input.proceeds ?? 0);
    const cashAccountId = input.cashAccountId ?? "1100";

    // Snapshot the books as of the disposal date so the JE truly closes out.
    const nbv = bookValue(asset, disposalDate);
    const accumulated = Math.max(0, Math.round((asset.cost - nbv) * 100) / 100);
    const gainLoss = Math.round((proceeds - nbv) * 100) / 100; // + = gain, − = loss

    // Build a balanced JE. Lines:
    //   DR Cash (proceeds)                          if proceeds > 0
    //   DR Accumulated Depreciation (1590)          if accumulated > 0
    //   DR Loss on Disposal (6995)                  if gainLoss < 0
    //   CR Asset cost account (1500)                always = cost
    //   CR Gain on Disposal (4995)                  if gainLoss > 0
    const lines: Array<{ accountId: string; amount: number; side: "debit" | "credit"; memo?: string }> = [];
    if (proceeds > 0) lines.push({ accountId: cashAccountId, amount: proceeds, side: "debit", memo: `Proceeds — ${asset.name}` });
    if (accumulated > 0) lines.push({ accountId: asset.accumulatedAccountId, amount: accumulated, side: "debit", memo: `Clear accumulated depreciation — ${asset.name}` });
    if (gainLoss < 0) lines.push({ accountId: "6995", amount: Math.abs(gainLoss), side: "debit", memo: `Loss on disposal — ${asset.name}` });
    lines.push({ accountId: "1500", amount: asset.cost, side: "credit", memo: `Remove asset cost — ${asset.name}` });
    if (gainLoss > 0) lines.push({ accountId: "4995", amount: gainLoss, side: "credit", memo: `Gain on disposal — ${asset.name}` });

    let journalEntry: JournalEntry | null = null;
    try {
      journalEntry = gl.postJournal({
        postingDate: disposalDate,
        description: `Disposal of ${asset.name} — ${input.reason}`,
        source: "adjustment",
        sourceRef: { kind: "asset_disposal", id: asset.id },
        idempotencyKey: `disposal:${asset.id}`,
        createdBy: "System (Fixed Assets)",
        lines,
      });
    } catch (e) {
      // If posting fails (e.g. closed period), surface to caller; do not mark disposed.
      throw e instanceof Error ? e : new Error("Failed to post disposal entry.");
    }

    const updated: FixedAsset = {
      ...asset,
      disposedAt: new Date().toISOString(),
      disposalDate,
      disposalReason: input.reason,
      disposalProceeds: proceeds,
      disposalGainLoss: gainLoss,
      disposalJournalEntryId: journalEntry?.id,
    };
    setAssets(prev => prev.map(a => (a.id === id ? updated : a)));
    return { asset: updated, journalEntry };
  }, [assets, bookValue, gl]);


  const postDepreciationFor = useCallback((assetId: string, period: string): JournalEntry | null => {
    const asset = assets.find(a => a.id === assetId);
    if (!asset) return null;
    if (asset.disposedAt && periodOf(asset.disposedAt) < period) return null;
    if (periodOf(asset.inServiceDate) > period) return null;
    const inServicePeriod = periodOf(asset.inServiceDate);
    const monthsElapsed = monthsBetween(`${inServicePeriod}-01`, `${period}-01`) + 1;
    if (monthsElapsed > asset.lifeMonths) return null; // fully depreciated
    if (asset.postedEntries.some(p => p.period === period)) return null; // idempotency
    const amount = monthlyDepreciation(asset);
    if (amount <= 0) return null;

    const je = gl.postJournal({
      postingDate: lastDayOfPeriod(period),
      description: `Depreciation — ${asset.name} (${period})`,
      source: "adjustment",
      sourceRef: { kind: "depreciation_run", id: `${asset.id}:${period}` },
      idempotencyKey: `depreciation:${asset.id}:${period}`,
      createdBy: "System (Depreciation)",
      lines: [
        { accountId: asset.expenseAccountId, amount, side: "debit",
          memo: `Depreciation ${period} — ${asset.name}` },
        { accountId: asset.accumulatedAccountId, amount, side: "credit",
          memo: `Depreciation ${period} — ${asset.name}` },
      ],
    });
    setAssets(prev => prev.map(a => a.id === assetId
      ? { ...a, lastPostedPeriod: period, postedEntries: [...a.postedEntries,
          { period, jeId: je.id, amount, postedAt: new Date().toISOString() }] }
      : a));
    return je;
  }, [assets, gl, monthlyDepreciation]);

  const postDepreciationThroughCurrentMonth = useCallback(() => {
    const posted: JournalEntry[] = [];
    let skipped = 0;
    const currentPeriod = periodOf(today());
    for (const a of assets) {
      if (a.disposedAt) continue;
      let p = a.lastPostedPeriod
        ? nextPeriod(a.lastPostedPeriod)
        : periodOf(a.inServiceDate);
      let safety = 0;
      while (p <= currentPeriod && safety++ < 180) {
        try {
          const je = postDepreciationFor(a.id, p);
          if (je) posted.push(je); else skipped++;
        } catch { skipped++; break; }
        p = nextPeriod(p);
      }
    }
    return { posted, skipped };
  }, [assets, postDepreciationFor]);

  const value = useMemo<FixedAssetsContextValue>(() => ({
    assets, createAsset, disposeAsset, monthlyDepreciation, bookValue,
    postDepreciationFor, postDepreciationThroughCurrentMonth,
  }), [assets, createAsset, disposeAsset, monthlyDepreciation, bookValue,
       postDepreciationFor, postDepreciationThroughCurrentMonth]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFixedAssets() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useFixedAssets must be used inside <FixedAssetsProvider>");
  return ctx;
}
